import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, X, Globe, Building2, Loader2 } from 'lucide-react';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { useExpenseCategories, useUpdateExpenseCategory } from '@/hooks/finance/useExpenseCategories';
import { 
  useDepartmentCostPositions, 
  useAssignCostPositionToDepartment, 
  useRemoveCostPositionFromDepartment,
  useToggleCategoryGlobal 
} from '@/hooks/finance/useDepartmentCostPositions';
import { ExpenseCategory } from '@/types/database/expense-categories';

export const DepartmentCategoryAssignment = () => {
  const { data: departments, isLoading: loadingDepts } = useDepartments();
  const { data: categories, isLoading: loadingCats } = useExpenseCategories();
  const { data: assignments, isLoading: loadingAssignments } = useDepartmentCostPositions();
  
  const assignMutation = useAssignCostPositionToDepartment();
  const removeMutation = useRemoveCostPositionFromDepartment();
  const toggleGlobalMutation = useToggleCategoryGlobal();
  
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Get only cost positions (recordable categories)
  const costPositions = useMemo(() => {
    return categories?.filter(c => c.is_recordable && c.is_active) || [];
  }, [categories]);

  // Get cost positions assigned to the selected department
  const assignedToSelectedDept = useMemo(() => {
    if (!selectedDepartment || !assignments) return [];
    return assignments.filter(a => a.department_id === selectedDepartment);
  }, [selectedDepartment, assignments]);

  // Get cost positions NOT assigned to the selected department (for the add dropdown)
  const availableForAssignment = useMemo(() => {
    if (!selectedDepartment) return [];
    const assignedIds = assignedToSelectedDept.map(a => a.category_id);
    return costPositions.filter(cp => !assignedIds.includes(cp.id));
  }, [selectedDepartment, assignedToSelectedDept, costPositions]);

  // Group assignments by department for the "By Department" view
  const assignmentsByDepartment = useMemo(() => {
    if (!departments || !assignments) return {};
    const grouped: Record<string, typeof assignments> = {};
    departments.forEach(dept => {
      grouped[dept.id] = assignments.filter(a => a.department_id === dept.id);
    });
    return grouped;
  }, [departments, assignments]);

  // Get global cost positions
  const globalCostPositions = useMemo(() => {
    return costPositions.filter(cp => (cp as any).is_global === true);
  }, [costPositions]);

  const handleAssign = async () => {
    if (!selectedDepartment || !selectedCategory) return;
    
    await assignMutation.mutateAsync({
      department_id: selectedDepartment,
      category_id: selectedCategory,
    });
    setSelectedCategory('');
  };

  const handleRemove = async (assignmentId: string) => {
    await removeMutation.mutateAsync(assignmentId);
  };

  const handleToggleGlobal = async (category: ExpenseCategory, isGlobal: boolean) => {
    await toggleGlobalMutation.mutateAsync({ id: category.id, is_global: isGlobal });
  };

  const isLoading = loadingDepts || loadingCats || loadingAssignments;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Department Cost Position Assignments
        </CardTitle>
        <CardDescription>
          Assign cost positions to specific departments. Global cost positions are available to all departments.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="by-department" className="space-y-4">
          <TabsList>
            <TabsTrigger value="by-department">By Department</TabsTrigger>
            <TabsTrigger value="global">Global Cost Positions</TabsTrigger>
          </TabsList>

          <TabsContent value="by-department" className="space-y-4">
            {/* Department selector and assignment form */}
            <div className="flex flex-col sm:flex-row gap-3 p-4 bg-muted/50 rounded-lg">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1 block">Select Department</Label>
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments?.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedDepartment && (
                <>
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1 block">Add Cost Position</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select cost position" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableForAssignment.length === 0 ? (
                          <SelectItem value="_none" disabled>
                            All cost positions are assigned
                          </SelectItem>
                        ) : (
                          availableForAssignment.map(cp => (
                            <SelectItem key={cp.id} value={cp.id}>
                              {cp.name} ({cp.code})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleAssign} 
                      disabled={!selectedCategory || assignMutation.isPending}
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Assign
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Assigned cost positions for selected department */}
            {selectedDepartment && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">
                  Assigned Cost Positions ({assignedToSelectedDept.length})
                </h4>
                {assignedToSelectedDept.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center bg-muted/30 rounded-lg">
                    No cost positions assigned to this department yet. 
                    Global cost positions will still be available.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {assignedToSelectedDept.map(assignment => (
                      <Badge 
                        key={assignment.id} 
                        variant="secondary" 
                        className="flex items-center gap-1 py-1 px-2"
                      >
                        {assignment.category?.name || 'Unknown'}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="ml-1 hover:text-destructive">
                              <X className="h-3 w-3" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove Assignment</AlertDialogTitle>
                              <AlertDialogDescription>
                                Remove "{assignment.category?.name}" from this department? 
                                Users in this department won't see this cost position when creating payment requests.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleRemove(assignment.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Overview of all departments */}
            {!selectedDepartment && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm">All Department Assignments</h4>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {departments?.map(dept => {
                    const deptAssignments = assignmentsByDepartment[dept.id] || [];
                    return (
                      <div 
                        key={dept.id} 
                        className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => setSelectedDepartment(dept.id)}
                      >
                        <div className="font-medium text-sm">{dept.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {deptAssignments.length} cost position{deptAssignments.length !== 1 ? 's' : ''} assigned
                        </div>
                        {deptAssignments.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {deptAssignments.slice(0, 3).map(a => (
                              <Badge key={a.id} variant="outline" className="text-xs py-0">
                                {a.category?.name}
                              </Badge>
                            ))}
                            {deptAssignments.length > 3 && (
                              <Badge variant="outline" className="text-xs py-0">
                                +{deptAssignments.length - 3} more
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="global" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Global cost positions are available to <strong>all departments</strong> when creating payment requests.
              Toggle the switch to make a cost position globally available.
            </p>

            <div className="space-y-2">
              {costPositions.map(cp => {
                const isGlobal = (cp as any).is_global === true;
                return (
                  <div 
                    key={cp.id} 
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {isGlobal && <Globe className="h-4 w-4 text-blue-500" />}
                      <div>
                        <div className="font-medium text-sm">{cp.name}</div>
                        <div className="text-xs text-muted-foreground">{cp.code}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`global-${cp.id}`} className="text-xs text-muted-foreground">
                        {isGlobal ? 'Global' : 'Not global'}
                      </Label>
                      <Switch
                        id={`global-${cp.id}`}
                        checked={isGlobal}
                        onCheckedChange={(checked) => handleToggleGlobal(cp, checked)}
                        disabled={toggleGlobalMutation.isPending}
                      />
                    </div>
                  </div>
                );
              })}

              {costPositions.length === 0 && (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No cost positions found. Create some cost positions first.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
